import { useEffect, useRef, useState } from 'react';

import { motion } from 'framer-motion';
import useTimer from 'hooks/useTimer';

import './style.scss';

const VISIBLE_COUNT = 3;
const ANIMATION_DURATION = 250;

const getOffset = () => {
  const w = window.innerWidth;
  if (w < 480) return 7;
  if (w < 768) return 10;
  return 15;
};

const StackCard = ({ card, index, offset, onClick }) => {
  const isFront = index === 0;

  const { timeDisplay } = useTimer({
    cardId: card?.assetId ?? card?.assetNo,
    mineCycleStartedAt: card?.mineCycleStartedAt,
    miningTime: card?.miningTime,
  });

  return (
    <motion.div
      animate={{ top: index * offset, right: index * offset, opacity: 1, scale: 1 }}
      className={`stack-card${isFront ? '' : ' stack-card-back'}`}
      initial={{ top: offset * (VISIBLE_COUNT + 1), right: offset * (VISIBLE_COUNT + 1), opacity: 0, scale: 0.95 }}
      onClick={onClick}
      style={{ zIndex: VISIBLE_COUNT - index }}
      transition={{ duration: ANIMATION_DURATION / 1000, ease: 'easeOut' }}
    >
      <img alt={isFront ? card?.asset : ''} aria-hidden={!isFront} className="stack-card-img" src={card?.imageUrl} />
      {isFront ? <span className="stack-card-timer">{timeDisplay || '00:00:00'}</span> : null}
    </motion.div>
  );
};

const RightDiagonalScrollStack = ({ items = [], onClick }) => {
  const [stack, setStack] = useState(items?.filter((item) => item?.isInUse) || []);
  const [offset, setOffset] = useState(getOffset);
  const containerRef = useRef(null);
  const lockRef = useRef(false);
  const stackRef = useRef(stack);

  // Keep stackRef updated with the latest stack state
  useEffect(() => {
    stackRef.current = stack;
  }, [stack]);

  // Handle window resize for offsets
  useEffect(() => {
    const onResize = () => setOffset(getOffset());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Add passive: false wheel listener for active scrolling
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const handleWheel = (e) => {
      if (stackRef.current.length <= 1) return; // Allow normal page scroll if stack is too small to rotate
      if (lockRef.current || Math.abs(e.deltaY) < 10) return;

      e.preventDefault();
      lockRef.current = true;

      setStack((prev) => {
        if (prev.length <= 1) return prev;
        const copy = [...prev];
        if (e.deltaY > 0) {
          const first = copy.shift();
          if (first !== undefined) copy.push(first);
        } else {
          const last = copy.pop();
          if (last !== undefined) copy.unshift(last);
        }
        return copy;
      });

      setTimeout(() => {
        lockRef.current = false;
      }, ANIMATION_DURATION);
    };

    node.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      node.removeEventListener('wheel', handleWheel);
    };
  }, []);

  // Bring clicked card to front
  const bringToFront = (index) => {
    const selected = stack[index];
    if (!selected) return;

    onClick?.(selected);

    setStack((prev) => {
      if (prev.length <= index || !prev[index]) return prev;
      const copy = [...prev];
      const [item] = copy.splice(index, 1);
      return [item, ...copy];
    });
  };

  // Update stack if items change
  useEffect(() => {
    setStack((prev) => {
      const next = items?.filter((item) => item?.isInUse) ?? [];
      if (prev.length === 0 && next.length === 0) return prev;
      return next;
    });
  }, [items]);

  const visibleCards = stack.slice(0, VISIBLE_COUNT);

  return (
    <div className="card-stack" ref={containerRef}>
      {visibleCards.map((card, index) => {
        if (!card) return null;
        return (
          <StackCard
            card={card}
            index={index}
            key={card.assetNo}
            offset={offset}
            onClick={() => bringToFront(index)}
          />
        );
      })}
    </div>
  );
};

export default RightDiagonalScrollStack;
